import type { AxiosError } from 'axios';
import { Center, Loader, Stack, Text, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
  useAcceptConnectionInvite,
  useCompleteConnectionInviteRegistration,
  usePeek
} from '@/api/generated/organisation-connections/organisation-connections';
import { useListCountries, useListCurrencies } from '@/api/generated/reference-data/reference-data';
import { IconFigmaArrowRight } from '@/assets/icons';
import {
  FormErrorMessage,
  MutationButton,
  PasswordField,
  SelectField,
  TextField
} from '@/components/common';
import { AuthCard } from '@/features/auth/components/AuthCard';
import { brandTextStyle } from '@/features/auth/components/authFieldStyles';
import { InvalidLinkCard } from '@/features/auth/components/InvalidLinkCard';
import { OrganisationsConnectedCard } from '@/features/auth/components/OrganisationsConnectedCard';
import { RegistrationSuccessCard } from '@/features/auth/components/RegistrationSuccessCard';
import { isValidTaxId } from '@/features/auth/components/taxIdFormats';
import { authErrorMessage } from '@/features/auth/utils/authErrorMessage';
import { buildCountryOptions } from '@/utils/format';

interface PeekResult {
  inviterOrgName?: string;
  invitedEmail?: string;
  accountExists?: boolean;
}

interface AcceptedConnectionResult {
  yourOrgName: string;
  partnerOrgName: string;
}

interface CompletedRegistrationResult {
  orgName: string;
  firstName: string;
  lastName: string;
  email: string;
  partnerOrgName: string;
}

// Isti obrazac kao registrationErrorMessage u RegisterForm — backend već vraća
// specifičan, lokalizirani razlog (nevažeći token, stanje računa promijenjeno,
// organizacije već povezane, OIB već postoji...), prikazujemo ga izravno.
const connectErrorMessage = (
  error: unknown,
  isPaused: boolean,
  t: (key: string) => string,
  fallback: string
): string =>
  authErrorMessage(error, isPaused, t, () => {
    const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
    return typeof backendMessage === 'string' ? backendMessage : fallback;
  });

const schema = z
  .object({
    organisationName: z.string().min(1),
    taxId: z.string().min(1),
    country: z.string().min(1),
    currency: z.string().min(1),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    password: z.string().min(8),
    confirmPassword: z.string().min(8)
  })
  .refine((values) => isValidTaxId(values.country, values.taxId), {
    message: 'invalidTaxId',
    path: ['taxId']
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'passwordsMustMatch',
    path: ['confirmPassword']
  });

type FormValues = z.infer<typeof schema>;

export const ConnectForm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const { data, isLoading, isError } = usePeek(token);
  const peek = data as PeekResult | undefined;
  const [acceptedConnection, setAcceptedConnection] = useState<AcceptedConnectionResult | null>(
    null
  );
  const [completedRegistration, setCompletedRegistration] =
    useState<CompletedRegistrationResult | null>(null);

  const acceptMutation = useAcceptConnectionInvite({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (data) => {
        const result = data as { orgAName?: string; orgBName?: string };
        const partnerOrgName = peek?.inviterOrgName ?? '';
        const yourOrgName =
          [result.orgAName, result.orgBName].find((name) => name && name !== partnerOrgName) ?? '';
        setAcceptedConnection({ yourOrgName, partnerOrgName });
      }
    }
  });

  const { data: countries, isLoading: countriesLoading } = useListCountries();
  const { data: currencies, isLoading: currenciesLoading } = useListCurrencies();
  const countryOptions = buildCountryOptions(countries);
  const currencyOptions =
    currencies?.map((c) => ({ value: c.code ?? '', label: `${c.code ?? ''} — ${c.name ?? ''}` })) ??
    [];

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: {
      organisationName: '',
      taxId: '',
      country: 'HR',
      currency: 'EUR',
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: ''
    }
  });

  const registerMutation = useCompleteConnectionInviteRegistration({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (_data, variables) => {
        setCompletedRegistration({
          orgName: variables.data.organisationName,
          firstName: variables.data.firstName,
          lastName: variables.data.lastName,
          email: peek?.invitedEmail ?? '',
          partnerOrgName: peek?.inviterOrgName ?? ''
        });
      }
    }
  });

  const handleSubmit = (values: FormValues) => {
    registerMutation.mutate({
      token,
      data: {
        organisationName: values.organisationName,
        taxId: values.taxId,
        country: values.country,
        currency: values.currency,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password
      }
    });
  };

  if (!token || isError) {
    return (
      <InvalidLinkCard
        title={t('connect.invalidTitle')}
        message={t('connect.invalidMessage')}
        retryLabel={t('connect.invalidRetry')}
        backTo="/login"
      />
    );
  }

  if (acceptedConnection) {
    return (
      <OrganisationsConnectedCard
        yourOrgName={acceptedConnection.yourOrgName}
        partnerOrgName={acceptedConnection.partnerOrgName}
      />
    );
  }

  if (completedRegistration) {
    return <RegistrationSuccessCard {...completedRegistration} />;
  }

  if (isLoading || !peek) {
    return (
      <AuthCard p={32} w={{ base: '100%', md: 420 }}>
        <Center py={20}>
          <Loader size="sm" />
        </Center>
      </AuthCard>
    );
  }

  if (peek.accountExists) {
    return (
      <AuthCard p={{ base: 24, md: 40 }} w={{ base: '100%', md: 440 }}>
        <Stack gap={20}>
          <Stack gap={6}>
            <Title order={2} fz={{ base: 20, md: 24 }} fw={700} style={brandTextStyle}>
              {t('connect.title')}
            </Title>
            <Text fz={{ base: 12.5, md: 13 }} style={{ color: 'var(--color-text-muted)' }}>
              {t('connect.caseBSubtitle', { org: peek.inviterOrgName ?? '' })}
            </Text>
          </Stack>

          {(acceptMutation.isError || acceptMutation.isPaused) && (
            <FormErrorMessage
              message={connectErrorMessage(
                acceptMutation.error,
                acceptMutation.isPaused,
                t,
                t('connect.submitError')
              )}
            />
          )}

          <MutationButton
            fullWidth
            rightSection={<IconFigmaArrowRight size={15} />}
            isPending={acceptMutation.isPending}
            isPaused={acceptMutation.isPaused}
            onClick={() => acceptMutation.mutate({ token })}
          >
            {t('connect.caseBAccept')}
          </MutationButton>
        </Stack>
      </AuthCard>
    );
  }

  return (
    <AuthCard p={{ base: 24, md: 40 }} w={{ base: '100%', md: 520 }}>
      <Stack gap={20}>
        <Stack gap={6}>
          <Title order={2} fz={{ base: 20, md: 24 }} fw={700} style={brandTextStyle}>
            {t('connect.title')}
          </Title>
          <Text fz={{ base: 12.5, md: 13 }} style={{ color: 'var(--color-text-muted)' }}>
            {t('connect.caseASubtitle', { org: peek.inviterOrgName ?? '' })}
          </Text>
        </Stack>

        {(registerMutation.isError || registerMutation.isPaused) && (
          <FormErrorMessage
            message={connectErrorMessage(
              registerMutation.error,
              registerMutation.isPaused,
              t,
              t('connect.submitError')
            )}
          />
        )}

        <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
          <Stack gap={12}>
            <TextField label={t('connect.invitedEmail')} value={peek.invitedEmail ?? ''} disabled />

            <TextField
              label={t('registration.companyName')}
              required
              {...form.getInputProps('organisationName')}
            />

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 12
              }}
            >
              <TextField
                label={t('registration.taxId')}
                placeholder={t(
                  form.values.country === 'DE'
                    ? 'registration.taxIdPlaceholderDe'
                    : 'registration.taxIdPlaceholder'
                )}
                required
                {...form.getInputProps('taxId')}
                error={form.errors.taxId ? t('registration.invalidTaxId') : undefined}
              />
              <SelectField
                label={t('registration.country')}
                allowDeselect={false}
                searchable
                disabled={countriesLoading}
                data={countryOptions}
                {...form.getInputProps('country')}
              />
            </div>

            <SelectField
              label={t('registration.currency')}
              allowDeselect={false}
              searchable
              disabled={currenciesLoading}
              data={currencyOptions}
              {...form.getInputProps('currency')}
            />

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 12
              }}
            >
              <TextField
                label={t('registration.firstName')}
                required
                {...form.getInputProps('firstName')}
              />
              <TextField
                label={t('registration.lastName')}
                required
                {...form.getInputProps('lastName')}
              />
            </div>

            <PasswordField
              label={t('registration.password')}
              required
              {...form.getInputProps('password')}
            />

            <PasswordField
              label={t('registration.confirmPassword')}
              required
              {...form.getInputProps('confirmPassword')}
              error={form.errors.confirmPassword ? t('registration.passwordsMustMatch') : undefined}
            />

            <MutationButton
              type="submit"
              fullWidth
              rightSection={<IconFigmaArrowRight size={15} />}
              isPending={registerMutation.isPending}
              isPaused={registerMutation.isPaused}
            >
              {t('connect.caseASubmit')}
            </MutationButton>
          </Stack>
        </form>
      </Stack>
    </AuthCard>
  );
};
